"use client"

import { motion, useInView, useReducedMotion } from "framer-motion"
import type { PropsWithChildren } from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useMotionReady } from "@/hooks/useMotionReady"

interface RevealProps extends PropsWithChildren {
  direction?: "up" | "down" | "left" | "right"
  delay?: number
  duration?: number
  amount?: number
  className?: string
}

export default function Reveal({
  children,
  direction = "up",
  delay = 0,
  duration = 0.5,
  amount = 0.2,
  className = "",
}: RevealProps) {
  const reduce = useReducedMotion()
  const ready = useMotionReady()
  const motionEnabled = ready && !reduce

  const ref = useRef<HTMLDivElement | null>(null)
  const inView = useInView(ref, {
    amount,
    once: true,
    margin: "0px 0px -10% 0px",
  })

  // Latch: once we decide to show, never hide again (prevents iOS IO timing flicker)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    if (!motionEnabled) {
      setShown(true)
      return
    }
    if (inView) setShown(true)
  }, [motionEnabled, inView])

  // Safari fallback: if IO is late or never fires, don't keep content hidden
  useEffect(() => {
    if (!motionEnabled || shown) return
    const t = window.setTimeout(() => setShown(true), 600)
    return () => window.clearTimeout(t)
  }, [motionEnabled, shown])

  const hidden = useMemo(() => {
    const dist = 30
    switch (direction) {
      case "up":
        return { opacity: 0, y: dist }
      case "down":
        return { opacity: 0, y: -dist }
      case "left":
        return { opacity: 0, x: dist }
      case "right":
        return { opacity: 0, x: -dist }
      default:
        return { opacity: 0, y: dist }
    }
  }, [direction])

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={motionEnabled ? "hidden" : false}
      animate={motionEnabled ? (shown ? "visible" : "hidden") : "visible"}
      variants={{
        hidden,
        visible: { opacity: 1, x: 0, y: 0 },
      }}
      transition={{ duration, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
      style={{
        willChange: "transform, opacity",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
      }}
    >
      {children}
    </motion.div>
  )
}
